// Intelligent avatar layout and sizing. Presentational: layout + wrapper motion only.

import React, { RefObject, useRef, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Avatar, { AvatarProps } from './Avatar';
import { useAvatarGridMotion } from './hooks';
import { useSystemWindowSize, useGridLayout } from '../../hooks';
import { calculatePlayerPositions, getEmptySlotPositions } from './utilities';
import type { Emotion } from '@airhead-games/shared/configs';
import { RoomPlayer } from '@airhead-games/shared/types';

/**
 * Function type for generating labels from player data
 */
type LabelFunction = (player: RoomPlayer) => string | undefined;
type LabelStyleFunction = (
  player: RoomPlayer
) => React.CSSProperties | undefined;

interface AvatarGridProps {
  players: RoomPlayer[]; // Array of players to display

  minSize?: number; // Minimum avatar size in pixels
  maxSize?: number; // Maximum avatar size in pixels

  rowGap?: number; // Gap between rows in pixels
  columnGap?: number; // Gap between columns in pixels
  maxRows?: number; // Maximum number of rows (0 = unlimited)
  maxColumns?: number; // Maximum number of columns (0 = unlimited)

  layoutType?: 'auto' | 'square' | 'row' | 'column' | 'grid'; // Layout strategy for the grid
  position?: 'top' | 'bottom' | 'left' | 'right' | 'center'; // Position of the grid within the container
  edgePadding?: number; // Padding from container edges when using edge positions

  enableFloatEntrance?: boolean; // Enable float-up entrance animations for avatars
  entranceStagger?: number; // Stagger delay between avatar entrances in milliseconds
  entranceDuration?: number; // Duration of entrance animation in milliseconds

  // Container element reference for sizing calculations. If not provided, uses window dimensions
  containerRef?: RefObject<HTMLElement | null>;
  // Function to generate top label content (text) for each player
  showTopLabel?: LabelFunction;
  // Function to generate bottom label content (text) for each player
  showBottomLabel?: LabelFunction;
  // Function to generate custom styles for top labels
  getTopLabelStyle?: LabelStyleFunction;
  // Function to generate custom styles for bottom labels
  getBottomLabelStyle?: LabelStyleFunction;
  // Optional map of player IDs to animation keys for top label animations
  topLabelAnimationKeys?: Map<string, string | number>;
  // Animation style for top label changes ('bounce' triggers on key change)
  topLabelAnimationType?: 'bounce';
  // State for scale animation - 'scaled' shows scaled/flashing effect for all players
  topLabelState?: 'normal' | 'scaled';
  // Props to pass through to each Avatar component
  avatarProps?: Partial<
    Omit<AvatarProps, 'player' | 'topLabel' | 'bottomLabel' | 'size'>
  >;

  // Optional override for avatar emotion
  // - Provide an Emotion to apply to all players
  // - Or provide a function to decide per-player (return null to fall back)
  // NOTE: This takes precedence over resolved state from useRoomPlayerAvatars
  emotionOverride?: Emotion | null | ((player: RoomPlayer) => Emotion | null);

  // Additional inline styles for the grid container
  style?: React.CSSProperties;
}

/**
 * AvatarGrid Component
 *
 * Presentational grid: computes layout and applies grid-level wrapper motion (via useAvatarGridMotion).
 *
 * @param props - AvatarGrid configuration
 * @returns Rendered avatar grid with responsive layout
 */
export default function AvatarGrid({
  players,
  containerRef,
  showTopLabel,
  showBottomLabel,
  getTopLabelStyle,
  getBottomLabelStyle,
  topLabelAnimationKeys,
  topLabelAnimationType = 'bounce',
  topLabelState,
  minSize = 120,
  maxSize = 240,
  rowGap = 16,
  columnGap = 16,
  layoutType = 'auto',
  position = 'center',
  edgePadding = 64,
  maxColumns = 0,
  maxRows = 0,
  enableFloatEntrance = false,
  entranceStagger = 500,
  entranceDuration = 5000,
  avatarProps = {},
  emotionOverride = null,

  style = {},
}: AvatarGridProps) {
  // Grid motion helper

  // Get window size for fallback when no container is provided
  const windowSize = useSystemWindowSize();

  // Create a fallback ref that simulates window dimensions
  const fallbackRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!containerRef && fallbackRef.current) {
      // Set the fallback ref dimensions to match window size
      fallbackRef.current.style.width = `${windowSize.width}px`;
      fallbackRef.current.style.height = `${windowSize.height}px`;
    }
  }, [containerRef, windowSize.width, windowSize.height]);

  // Use provided containerRef or fallback
  const effectiveContainerRef = containerRef || fallbackRef;

  // Prepare grid-level motion utilities (initialized after container dims are known)

  // Calculate optimal grid layout
  const {
    itemSize: avatarSize,
    gridColumns,
    gridRows,
    rowGap: calculatedRowGap,
    columnGap: calculatedColumnGap,
  } = useGridLayout(players.length, effectiveContainerRef, {
    minSize,
    maxSize,
    rowGap,
    columnGap,
    layoutType,
    maxColumns,
    maxRows,
  });

  // Calculate container dimensions for position calculations
  const containerWidth =
    effectiveContainerRef.current?.clientWidth || windowSize.width;
  const containerHeight =
    effectiveContainerRef.current?.clientHeight || windowSize.height;

  // Prepare grid-level motion utilities (requires container dimensions)
  const { getItemMotion, getLabelFadeInDelay } = useAvatarGridMotion(players, {
    containerWidth,
    containerHeight,
    itemSize: avatarSize,
    enableFloatEntrance,
    entranceDuration,
    entranceStagger,
  });

  // Calculate absolute positions for all players
  const playerPositions = useMemo(
    () =>
      calculatePlayerPositions(
        players,
        {
          containerWidth,
          containerHeight,
          avatarSize,
          rowGap: calculatedRowGap,
          columnGap: calculatedColumnGap,
          gridColumns,
          gridRows,
        },
        layoutType,
        position,
        edgePadding
      ),
    [
      players,
      containerWidth,
      containerHeight,
      avatarSize,
      calculatedRowGap,
      calculatedColumnGap,
      gridColumns,
      gridRows,
      layoutType,
      position,
      edgePadding,
    ]
  );

  // Get empty slot positions for grid layout
  const emptySlots = useMemo(
    () =>
      layoutType === 'grid'
        ? getEmptySlotPositions(playerPositions, {
            containerWidth,
            containerHeight,
            avatarSize,
            rowGap: calculatedRowGap,
            columnGap: calculatedColumnGap,
            gridColumns,
            gridRows,
          })
        : [],
    [
      layoutType,
      playerPositions,
      containerWidth,
      containerHeight,
      avatarSize,
      calculatedRowGap,
      calculatedColumnGap,
      gridColumns,
      gridRows,
    ]
  );

  return (
    <>
      {/* Hidden fallback container for window size measurements when no containerRef provided */}
      {!containerRef && (
        <div
          ref={fallbackRef}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            visibility: 'hidden',
            pointerEvents: 'none',
            zIndex: -1,
          }}
        />
      )}

      <div
        className="avatar-grid"
        style={{
          position: containerRef ? 'relative' : 'fixed',
          top: containerRef ? 'auto' : 0,
          left: containerRef ? 'auto' : 0,
          width: containerRef ? '100%' : `${containerWidth}px`,
          height: containerRef ? '100%' : `${containerHeight}px`,
          pointerEvents: 'none',
          ...style,
        }}
      >
        <AnimatePresence mode="popLayout">
          {/* Render empty slots for grid layout */}
          {layoutType === 'grid' &&
            emptySlots.map((emptyPos, index) => (
              <div
                key={`empty-${index}`}
                style={{
                  position: 'absolute',
                  left: emptyPos.x,
                  top: emptyPos.y,
                  width: avatarSize,
                  height: avatarSize,
                  // Invisible placeholder
                  pointerEvents: 'none',
                }}
              />
            ))}

          {/* Render all players with absolute positioning */}
          {playerPositions.map(({ player, position }, index) => {
            const topLabel = showTopLabel?.(player);
            const bottomLabel = showBottomLabel?.(player);
            const topLabelStyle = getTopLabelStyle?.(player);
            const bottomLabelStyle = getBottomLabelStyle?.(player);
            const labelFadeInDelay = getLabelFadeInDelay(
              player.playerId,
              index
            );
            const topLabelAnimationKey = topLabelAnimationKeys?.get(
              player.playerId
            );

            return (
              <motion.div
                key={player.playerId}
                style={{
                  position: 'absolute',
                  width: avatarSize,
                  height: avatarSize,
                }}
                {...getItemMotion(player.playerId, position, index)}
              >
                <Avatar
                  player={player}
                  size={avatarSize}
                  emotion={
                    // 1) Explicit override: accept function or constant
                    (typeof emotionOverride === 'function'
                      ? emotionOverride(player)
                      : emotionOverride) ||
                    // 2) Final default (Avatar will handle emotion resolution)
                    'happy'
                  }
                  topLabel={topLabel}
                  bottomLabel={bottomLabel}
                  topLabelStyle={topLabelStyle}
                  bottomLabelStyle={bottomLabelStyle}
                  topLabelAnimationKey={topLabelAnimationKey}
                  topLabelAnimationType={topLabelAnimationType}
                  topLabelState={topLabelState}
                  labelFadeInDelay={labelFadeInDelay}
                  enableFloat={avatarProps.enableFloat !== false}
                  {...avatarProps}
                />
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>
    </>
  );
}
